import {
  useWSModalCustom,
  WSInfoBox,
  WSList,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import { DataViewSection } from "../../../shared/components/DataView";
import { useMutationWithdrawCancellationPlanEnrollment } from "../../../query/benefits";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { IPlanType } from "../../../services/api/planEnrollment/types";
import { useQueryInsuranceCoverages } from "../../../query/insuranceCoverage/queries/useQueryInsuranceCoverages";
import { WSQueries } from "../../../query/WSQuery";
import flatten from "lodash/flatten";
import { selectorPolicyNameByPolicyType } from "../selectors/selectorPolicyNameByPolicyType";
import {
  OAI_KEY,
  PGLI_KEY,
  PLAN_DETAILS
} from "../../Benefits/utils/constants";

const PGLI_CONFIG = PLAN_DETAILS[PGLI_KEY];
const OAI_CONFIG = PLAN_DETAILS[OAI_KEY];

type Props = {
  onClose: () => void;
  planType: IPlanType;
  planEnrollmentId: string;
};

const FEE_LABELS: Record<IPlanType, string> = {
  WingspanOccupationalAccidentInsurance: OAI_CONFIG.monthlyPremiumFormatted,
  WingspanProfessionalLiabilityAndGeneralLiabilityInsurance:
    PGLI_CONFIG.monthlyPremiumFormatted,
  MeridioHealth: " "
};

const ModalStopCancellation: React.FC<Props> = ({
  onClose,
  planEnrollmentId,
  planType
}) => {
  const [
    withdrawCancellationPlanEnrollment,
    withdrawCancellationPlanEnrollmentMeta
  ] = useMutationWithdrawCancellationPlanEnrollment();

  const qInsuranceCoverages = useQueryInsuranceCoverages({
    filter: {
      sourceId: planEnrollmentId
    }
  });

  return (
    <WSModal
      title="Keep enrollment"
      size="M"
      onClose={onClose}
      actions={{
        alignment: "fill",
        buttons: [
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Tertiary"
          },
          {
            label: "Yes, keep my enrollment",
            onClick: async () => {
              await withdrawCancellationPlanEnrollment(planEnrollmentId);
              onClose();
            },
            loading: withdrawCancellationPlanEnrollmentMeta.isLoading,
            kind: "Primary"
          }
        ]
      }}
    >
      <WSList gap="L">
        <WSText.ParagraphSm color="gray500">
          Please confirm you want to stop your previous cancellation request and
          keep your current enrollment.
        </WSText.ParagraphSm>
        <WSQueries queries={{ qInsuranceCoverages }}>
          {({ qInsuranceCoveragesData }) => {
            const insurances = flatten(qInsuranceCoveragesData.pages);

            return (
              <WSList gap="M">
                {insurances.map(insurance => (
                  <DataViewSection
                    key={insurance.insuranceCoverageId}
                    gridColumns={2}
                    title={
                      insurance.title
                        ? insurance.title
                        : selectorPolicyNameByPolicyType(
                            insurance.policyType,
                            planType
                          )
                    }
                    data={[
                      {
                        label: "Insurer",
                        value: insurance.insurerName
                      },
                      {
                        label: "Policy number",
                        value: insurance.policyNumber
                      }
                    ]}
                  />
                ))}
              </WSList>
            );
          }}
        </WSQueries>

        <WSInfoBox title="Reminder">
          By keeping you enrollment, the current premiums {FEE_LABELS[planType]}{" "}
          will continue to be deducted automatically to maintain your coverage.
        </WSInfoBox>

        <WSErrorMessage
          error={withdrawCancellationPlanEnrollmentMeta.error}
          contextKey="Other"
        />
      </WSList>
    </WSModal>
  );
};

export const useModalStopCancellation = () => {
  return useWSModalCustom(ModalStopCancellation);
};
